/**
 * Menu Type Definitions
 *
 * This file contains TypeScript interfaces and constants for menu routes and related data structures.
 */

import { isMultiOrgLikeVariant } from "@/utils/partnerVariant";
import type { LoginInfo } from "@/types/LoginInfo";

/**
 * The organisation type as the sidebar needs it (STIC-15615).
 *
 * `organisationType` on its own is too coarse: a PARTNER organisation is either
 * a *channel* partner or a *multi-org* partner, and the two get different
 * menus. The login payload distinguishes them through the partner variant
 * (`LoginPartnerInfo.partnerVariant`, STIC-16004): CHANNEL_PARTNER is the channel
 * partner; MULTI_ORG, SENSOLUS and MULTI_ORG_INDIRECT are multi-org-like. What each
 * variant may see of its business with Sensolus lives in `utils/directBusiness.ts`.
 *
 * Menu entries declare their visibility against these four values directly, in
 * their own `view:` / `disabled:` expressions. The spreadsheet
 * `platform navigation menus.xlsx` ("Admin menu" sheet) is the design document
 * those expressions implement, one row per menu item:
 *
 * - Which menu you are in supplies the level — `useOwnLevelMenu` is the sheet's
 *   "Own-level" column, `useCrossLevelMenu` its "Cross-level" columns.
 * - `NORMAL` is deliberately absent from the sheet, so a NORMAL login is left to
 *   its feature keys and permissions. Where an entry is hidden for the other
 *   three, the expression says `isNormalLogin` rather than `false`.
 * - "Disabled" means listed but greyed and still clickable; the page behind it
 *   renders `<NotAvailableAtThisLevel />`. See {@link MenuRoute.disabled}.
 */
export const OrgTypeConst = {
  /** Sensolus itself. */
  SYSTEM: "SYSTEM",
  /** Channel partner (variant CHANNEL_PARTNER), commercial or not. */
  PARTNER: "PARTNER",
  /** Multi-org-like partner (variant MULTI_ORG, SENSOLUS or MULTI_ORG_INDIRECT). */
  MULTI_ORG: "MULTI_ORG",
  /** A single customer organisation. */
  NORMAL: "NORMAL",
} as const;

export type OrgType = (typeof OrgTypeConst)[keyof typeof OrgTypeConst];

/**
 * Resolves the logged-in user's organisation to its {@link OrgType}.
 *
 * Returns `null` only when the login has no organisation yet. A PARTNER
 * organisation whose variant has not loaded reads as `PARTNER`, the stricter of
 * the two partner kinds in every row where they differ - the same way
 * `directBusiness.ts` fails closed on a missing variant.
 */
export const getEnhancedOrgType = (loginInfo: LoginInfo | null | undefined): OrgType | null => {
  switch (loginInfo?.organisation?.organisationType) {
    case "SYSTEM":
      return OrgTypeConst.SYSTEM;
    case "PARTNER":
      return isMultiOrgLikeVariant(loginInfo?.partner?.partnerVariant)
        ? OrgTypeConst.MULTI_ORG
        : OrgTypeConst.PARTNER;
    case "NORMAL":
      return OrgTypeConst.NORMAL;
    default:
      return null;
  }
};

/**
 * Menu type enumeration constant
 */
export const MenuTypeConst = {
  MAIN: "MAIN",
  ADMIN: "ADMIN",
  ALL: "ALL",
} as const;

/**
 * Menu type values - "MAIN" | "ADMIN" | "ALL"
 */
export type MenuType = typeof MenuTypeConst[keyof typeof MenuTypeConst];

/**
 * Which hook produced the menu currently rendered in the sidebar.
 *
 * `MenuType` cannot answer this on its own: several hooks report the same
 * MenuTypeConst. Read by `NavList.buildItemHref` to decide the org parameter a
 * sidebar item links to — own-level items carry none, cross-level items carry
 * `scope=all` when no org is sliced.
 */
export const MenuSourceConst = {
  HOME: "HOME",
  /** "own-level" in STIC-15615 terms — the menu of the org actually sliced. */
  OWN: "OWN",
  /** "cross-level" — the "All" pill, spanning every org the user can see. */
  CROSS: "CROSS",
} as const;

export type MenuSource = typeof MenuSourceConst[keyof typeof MenuSourceConst];

/**
 * Represents a single menu route item
 */
export interface MenuRoute {
  to?: string;
  layout?: string;
  name?: React.ReactNode;
  view?: boolean;
  subPages?: string[];
  isForceAvailableMenu?: boolean;
  collapse?: boolean;
  state?: string;
  icon?: React.ReactNode;
  views?: MenuRoute[];
  isLabFeature?: boolean;
  topOverview?: boolean;
  /**
   * Marks a top-level section as belonging to the engineering block at the
   * bottom of the SYSTEM own-level menu (STIC-15790). NavList tints those
   * section headers and opens the run with a heading, so a colleague scrolling
   * the menu can see where the deeply technical pages start. Set it on every
   * section in the run — the heading is drawn before the first one still
   * visible after pruning, so the block survives sections being hidden.
   */
  engineeringZone?: boolean;
  /**
   * Computed by the menu hook: the item does not apply at the currently
   * selected level (wiki Rule #5 — e.g. Diagnostic at cross-level "All",
   * Location settings with a partner org sliced; STIC-15615 R9/R11).
   * NavList greys it out but keeps it clickable; the page behind it should
   * render <NotAvailableAtThisLevel /> and explain.
   */
  disabled?: boolean;
}

/**
 * Default path object with pathname and search params
 */
export interface DefaultPath {
  pathname: string;
  search: string;
}

/**
 * Return type of useAvailibleMenu hook
 */
export interface AvailibleMenuData {
  routes?: MenuRoute[];
  /**
   * `routes` with every hidden entry removed — what NavList renders. Sections
   * left without a single visible child are removed too.
   *
   * Kept separate from `routes` because the two answer different questions:
   * `routes`/`allPaths` must still know about hidden entries so a deep link to
   * one resolves to a menu, while the sidebar must not draw them.
   */
  renderRoutes?: MenuRoute[];
  /** Which hook built this menu — see MenuSourceConst. */
  menuSource?: MenuSource;
  availableMenuPaths: Record<string, MenuRoute>;
  forceMenuPaths?: Record<string, MenuRoute>;
  allPaths: Record<string, MenuRoute>;
  defaultPath: DefaultPath | string;
  isAdminMenuAvailable?: boolean;
}

/**
 * Default export for backwards compatibility with JavaScript files
 */
export default MenuTypeConst;
